import React, { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, Mail, Phone, Trash2, RefreshCw, Inbox, ArrowLeft, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ocg_admin_token';

const formatDate = (iso) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Falha no login');
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-8 space-y-6" data-testid="admin-login-form">
        <div className="flex flex-col items-center gap-3">
          <img src="/logo-nova.png" alt="O.C. GOMES" className="h-20 w-20 rounded-full" />
          <h1 className="text-2xl font-bold text-white">Painel <span className="text-[#FF6B00]">Admin</span></h1>
          <p className="text-gray-400 text-sm">Mensagens recebidas pelo site</p>
        </div>
        <div>
          <label htmlFor="admin-password" className="block text-white font-medium mb-2">Senha</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password-input"
              className="w-full pl-10 pr-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-md text-white focus:outline-none focus:border-[#FF6B00] transition-colors"
              placeholder="Digite a senha"
            />
          </div>
        </div>
        {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full bg-[#FF6B00] text-white py-3 rounded-md hover:bg-[#ff8534] transition-colors font-semibold disabled:opacity-60"
        >
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
        <button type="button" onClick={() => window.location.assign('/')} className="w-full text-gray-400 hover:text-[#FF6B00] text-sm flex items-center justify-center gap-2 transition-colors">
          <ArrowLeft size={16} /> Voltar ao site
        </button>
      </form>
    </div>
  );
};

const MessageCard = ({ msg, onDelete }) => (
  <article className="bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#FF6B00]/60 rounded-lg p-6 transition-colors" data-testid={`message-card-${msg.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="text-white font-semibold text-lg">{msg.name}</h3>
        <time className="text-gray-500 text-xs">{formatDate(msg.created_at)}</time>
      </div>
      <button
        onClick={() => onDelete(msg.id)}
        data-testid={`delete-message-${msg.id}`}
        className="text-gray-500 hover:text-red-400 transition-colors p-2"
        aria-label="Excluir mensagem"
      >
        <Trash2 size={18} />
      </button>
    </div>
    <p className="text-gray-300 whitespace-pre-wrap mb-5 leading-relaxed">{msg.message}</p>
    <div className="flex flex-wrap gap-3 text-sm">
      {msg.vehicle && (
        <span className="flex items-center gap-2 text-[#FF6B00]"><Truck size={16} /> {msg.vehicle}</span>
      )}
      {msg.email && (
        <a href={`mailto:${msg.email}`} className="flex items-center gap-2 text-gray-400 hover:text-[#FF6B00] transition-colors">
          <Mail size={16} /> {msg.email}
        </a>
      )}
      <a href={`https://wa.me/55${msg.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#25D366] transition-colors">
        <Phone size={16} /> {msg.phone}
      </a>
    </div>
  </article>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMessages([]);
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return logout();
      setMessages(await res.json());
    } catch {
      toast({ title: 'Erro', description: 'Não foi possível carregar as mensagens.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleLogin = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta mensagem?')) return;
    const res = await fetch(`${API}/api/contact/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast({ title: 'Mensagem excluída' });
    }
  };

  if (!token) return <LoginForm onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      <header className="border-b border-[#2a2a2a] bg-[#111111]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-nova.png" alt="O.C. GOMES" className="h-12 w-12 rounded-full" />
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">Painel Admin</h1>
              <p className="text-gray-500 text-xs">Mensagens do formulário de contato</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchMessages} data-testid="admin-refresh" className="text-gray-400 hover:text-[#FF6B00] p-2 transition-colors" aria-label="Atualizar">
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white px-3 py-2 text-sm transition-colors hidden sm:block">Ver site</button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#FF6B00] text-white px-4 py-2 rounded-md text-sm transition-colors">
              <LogOut size={16} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-white text-2xl font-bold">
            Mensagens <span className="text-[#FF6B00]" data-testid="message-count">({messages.length})</span>
          </h2>
        </div>
        {!loading && messages.length === 0 ? (
          <div className="text-center py-24 text-gray-500" data-testid="admin-empty-state">
            <Inbox size={48} className="mx-auto mb-4 text-[#2a2a2a]" />
            Nenhuma mensagem recebida ainda.
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => <MessageCard key={msg.id} msg={msg} onDelete={handleDelete} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
